/**
 * DataRightsPanel — inzage en verwijdering, voor de ouder.
 *
 * De AVG geeft een ouder twee rechten die tot nu toe alleen in de database
 * bestonden: inzage (art. 15) en verwijdering (art. 17). De RPC's stonden er
 * al; er was geen knop. Een recht zonder knop is voor de ouder die het wil
 * uitoefenen niet te onderscheiden van een recht dat er niet is.
 *
 * DRIE ONTWERPKEUZES
 *
 * 1. Inzage is één klik, verwijdering is er drie. Dat is geen inconsistentie
 *    maar het verschil tussen omkeerbaar en onherstelbaar. Een export die je
 *    niet wilde gooi je weg; een verwijdering die je niet wilde is weg.
 *
 * 2. "Gegevens wissen" en "account opheffen" zijn gescheiden. Ze samenvoegen
 *    zou betekenen dat een ouder die alleen wil opschonen zijn kind ook zijn
 *    inlog afneemt — en dat kind kan er dan niet meer oefenen.
 *
 * 3. Bij verwijderen tikt de ouder de naam van het kind over. Dat is geen
 *    ritueel: het dwingt hem te lezen wélk kind hij verwijdert. Met twee
 *    kinderen in de lijst is één misklik anders genoeg.
 */

import React, { useState } from 'react';
import { Download, Trash2, Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import { exportLearnerData, deleteLearnerData, downloadExport } from '../../../data/supabase/dataRights';
import { deleteAssessmentDataForLearner } from '../../../assessment/utils/assessmentStorage';
import { error as logError } from '../../../utils/logger';
import type { LearnerAccount } from '../../../accounts';
interface DataRightsPanelProps {
  linkedLearners: LearnerAccount[];
}
export function DataRightsPanel({
  linkedLearners
}: DataRightsPanelProps) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<LearnerAccount | null>(null);
  const [confirmName, setConfirmName] = useState('');
  if (linkedLearners.length === 0) return null;
  const handleExport = async (learner: LearnerAccount) => {
    setBusyId(learner.id);
    const res = await exportLearnerData(learner.id);
    setBusyId(null);
    if (!res.ok) {
      toast.error(res.message ?? 'Ophalen is niet gelukt.');
      return;
    }
    downloadExport(res.data, learner.displayName ?? 'kind');
    toast.success(`De gegevens van ${learner.displayName} zijn gedownload.`);
  };
  const handleConfirmDelete = async () => {
    const learner = pendingDelete;
    if (!learner) return;
    setPendingDelete(null);
    setConfirmName('');
    setBusyId(learner.id);

    // Alleen de gegevens, niet het profiel. Het kind houdt zijn account en
    // kan gewoon blijven oefenen; wat weg gaat is wat wij over hem bewaard
    // hebben. Account opheffen is een ander verzoek en hoort een eigen,
    // even expliciete handeling te zijn.
    const res = await deleteLearnerData(learner.id, false);
    if (res.ok) {
      // De RPC wist de serverkopie. Wat op dít apparaat staat blijft anders
      // staan — hetzelfde gat als bij het intrekken van toestemming. Zonder
      // deze stap ziet de ouder zijn zojuist gewiste toetsuitslagen gewoon
      // nog in het overzicht.
      await deleteAssessmentDataForLearner(learner.id).catch(err => logError('[DataRights] Lokale toetsdata wissen mislukt', err));
    }
    setBusyId(null);
    if (!res.ok) {
      toast.error(res.message ?? 'Verwijderen is niet gelukt.');
      return;
    }
    const total = Object.values(res.deleted ?? {}).reduce((sum, n) => sum + (typeof n === 'number' ? n : 0), 0);
    toast.success(total > 0 ? `${total} gegevens van ${learner.displayName} zijn verwijderd. ${learner.displayName} kan gewoon blijven oefenen.` : `Er waren geen bewaarde gegevens van ${learner.displayName} om te verwijderen.`, {
      duration: 8000
    });
  };
  return <section className="p-6 bg-white rounded-2xl border border-slate-200 shadow-sm" aria-labelledby="datarights-heading" data-id="element-8446" data-mp="o2zz4qfzz2zo">
      
      <h3 id="datarights-heading" className="font-bold text-slate-900 mb-1 flex items-center gap-2" data-id="element-8447" data-mp="1lp093rik5wha">
        
        <ShieldAlert className="w-4 h-4 text-slate-500" data-id="element-8448" data-mp="1gpnl33fyajd4" />
        Gegevens inzien of verwijderen
      </h3>
      <p className="text-sm text-slate-600 mb-5" data-id="element-8449" data-mp="1ukzgze5uefvm">
        Je mag altijd opvragen welke gegevens wij van je kind bewaren, en je
        mag ze laten verwijderen. Je kind houdt daarbij zijn account en kan
        gewoon blijven oefenen.
      </p>

      <ul className="space-y-3" data-id="element-8450" data-mp="xtno1e848pmo">
        {linkedLearners.map(learner => {
        const isBusy = busyId === learner.id;
        return <li key={learner.id} className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-xl border border-slate-200" data-id="element-8451" data-mp="1bu880dprorhr">
              
              <span className="font-semibold text-slate-800" data-id="element-8452" data-mp="2n7arphn5xx65">
                {learner.displayName}
              </span>
              <div className="flex items-center gap-2" data-id="element-8453" data-mp="2i7pgo8upxyd2">
                <Button variant="secondary" disabled={isBusy} onClick={() => handleExport(learner)} aria-label={`Gegevens van ${learner.displayName} downloaden`} data-id="element-8454" data-mp="2qprezh1p7whi">
                  
                  {isBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" data-id="element-8455" data-mp="2up09io4kbye4" /> : <Download className="w-4 h-4 mr-2" data-id="element-8456" data-mp="2gslnw8t345ha" />}
                  Downloaden
                </Button>
                <Button variant="secondary" disabled={isBusy} onClick={() => {
              setConfirmName('');
              setPendingDelete(learner);
            }} className="text-rose-600 hover:bg-rose-50" aria-label={`Gegevens van ${learner.displayName} verwijderen`} data-id="element-8457" data-mp="2lrg5x8j89q0c">
                  
                  <Trash2 className="w-4 h-4 mr-2" data-id="element-8458" data-mp="2zpmufy2aednc" />
                  Verwijderen
                </Button>
              </div>
            </li>;
      })}
      </ul>

      <ConfirmDialog open={pendingDelete !== null} title="Gegevens verwijderen" message={pendingDelete ? [`Alle bewaarde gegevens van ${pendingDelete.displayName} worden verwijderd: oefenvoortgang, toetsuitslagen en rapporten.`, '', `${pendingDelete.displayName} houdt zijn of haar account en kan gewoon blijven oefenen, maar begint weer bij het begin.`, '', 'Dit kan niet ongedaan gemaakt worden.', '', `Tik ter bevestiging de naam over: ${pendingDelete.displayName}`].join('\n') : ''} confirmLabel="Definitief verwijderen" cancelLabel="Annuleren" variant="danger"
    // De knop blijft uit tot de naam klopt. Hoofdletterongevoelig, want
    // dit is een leesbevestiging en geen spelproef.
    confirmDisabled={confirmName.trim().toLowerCase() !== (pendingDelete?.displayName ?? '').trim().toLowerCase()} onConfirm={handleConfirmDelete} onCancel={() => {
      setPendingDelete(null);
      setConfirmName('');
    }} data-id="element-8459" data-mp="3sfdaicnhbpm3">
        
        <Input label={`Naam van je kind`} value={confirmName} onChange={e => setConfirmName(e.target.value)} placeholder={pendingDelete?.displayName ?? ''} autoComplete="off" data-id="element-8460" data-mp="39f389pc6m4nq" />
        
      </ConfirmDialog>
    </section>;
}